import * as React from 'react'

import { Program, PersonalScheduleProgram, isExerciseItem } from '@/domain'

import { startOfDay } from '@/lib/calendarWeekdays'
import { getActiveScheduleEntries } from '@/lib/activeSchedule'
import { resolveExerciseReference } from '@/lib/scheduleReference'

import { AppContext } from '@/components/base/appcontext'
import LinkWithState from '@/components/base/link'

import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Typography } from '@/components/ui/typography'

import { Plus, Search, ChevronRight, Calendar } from 'lucide-react'
import { exerciseLabel } from '@/lib/programme/exerciseLabel'

type ListItemProps = {
    isActive?: boolean
    workoutList: Program[]
    personalSchedule: PersonalScheduleProgram[]
}

function ScheduleOverflow({
    nextDate,
    today,
    fallbackLabel,
    todayLabel
}: {
    nextDate: number
    today: number
    fallbackLabel: string
    todayLabel: string
}) {
    const isToday = nextDate === today
    return (
        <div className="flex flex-col items-center justify-center px-3 min-w-[4.5rem] border-l border-border/50">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground mb-0.5" />
            <span
                className={`text-[11px] font-semibold leading-tight text-center ${isToday ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
            >
                {nextDate && nextDate > today
                    ? new Date(nextDate).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                      })
                    : fallbackLabel}
                {isToday && todayLabel}
            </span>
        </div>
    )
}

function AnimatedGradientCover() {
    const gradients = [
        {
            id: 'Gradient1',
            fx: '0.441602%',
            dur: '34s',
            values: '0%;3%;0%',
            color: '255, 0, 255'
        },
        {
            id: 'Gradient2',
            fx: '2.68147%',
            dur: '23.5s',
            values: '0%;3%;0%',
            color: '255, 255, 0'
        },
        {
            id: 'Gradient3',
            fx: '0.836536%',
            dur: '21.5s',
            values: '0%;3%;0%',
            color: '0, 255, 255'
        },
        {
            id: 'Gradient4',
            fx: '4.56417%',
            dur: '23s',
            values: '0%;5%;0%',
            color: '0, 255, 0'
        },
        {
            id: 'Gradient5',
            fx: '2.65405%',
            dur: '24.5s',
            values: '0%;5%;0%',
            color: '0,0,255'
        },
        {
            id: 'Gradient6',
            fx: '0.981338%',
            dur: '25.5s',
            values: '0%;5%;0%',
            color: '255,0,0'
        }
    ]

    const rects = [
        {
            x: '13.744%',
            y: '1.18473%',
            fill: 'url(#Gradient1)',
            transform: 'rotate(334.41 50 50)',
            animX: { dur: '20s', values: '25%;0%;25%' },
            animY: { dur: '21s', values: '0%;25%;0%' },
            rotateDur: '7s',
            rotateFrom: '0 50 50',
            rotateTo: '360 50 50'
        },
        {
            x: '-2.17916%',
            y: '35.4267%',
            fill: 'url(#Gradient2)',
            transform: 'rotate(255.072 50 50)',
            animX: { dur: '23s', values: '-25%;0%;-25%' },
            animY: { dur: '24s', values: '0%;50%;0%' },
            rotateDur: '12s',
            rotateFrom: '0 50 50',
            rotateTo: '360 50 50'
        },
        {
            x: '9.00483%',
            y: '14.5733%',
            fill: 'url(#Gradient3)',
            transform: 'rotate(139.903 50 50)',
            animX: { dur: '25s', values: '0%;25%;0%' },
            animY: { dur: '12s', values: '0%;25%;0%' },
            rotateDur: '9s',
            rotateFrom: '360 50 50',
            rotateTo: '0 50 50'
        }
    ]

    return (
        <div className="absolute inset-0">
            <svg
                key="active-prog-gradient"
                viewBox="0 0 100 100"
                preserveAspectRatio="xMidYMid slice"
            >
                <defs>
                    {gradients.map((g) => (
                        <radialGradient
                            key={g.id}
                            id={g.id}
                            cx="50%"
                            cy="50%"
                            fx={g.fx}
                            fy="50%"
                            r=".5"
                        >
                            <animate
                                attributeName="fx"
                                dur={g.dur}
                                values={g.values}
                                repeatCount="indefinite"
                            />
                            <stop
                                offset="0%"
                                stopColor={`rgba(${g.color}, 1)`}
                            />
                            <stop
                                offset="100%"
                                stopColor={`rgba(${g.color}, 0)`}
                            />
                        </radialGradient>
                    ))}
                </defs>
                {rects.map((r, i) => (
                    <rect
                        key={i}
                        x={r.x}
                        y={r.y}
                        width="100%"
                        height="100%"
                        fill={r.fill}
                        transform={r.transform}
                    >
                        <animate
                            attributeName="x"
                            dur={r.animX.dur}
                            values={r.animX.values}
                            repeatCount="indefinite"
                        />
                        <animate
                            attributeName="y"
                            dur={r.animY.dur}
                            values={r.animY.values}
                            repeatCount="indefinite"
                        />
                        <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from={r.rotateFrom}
                            to={r.rotateTo}
                            dur={r.rotateDur}
                            repeatCount="indefinite"
                        />
                    </rect>
                ))}
            </svg>
        </div>
    )
}

function ListItem(props: ListItemProps) {
    const appContext = React.useContext(AppContext)

    const today = startOfDay(new Date().getTime())
    const emptySchedulePrompt = (
        <div className="grid grid-cols-2 gap-2">
            <LinkWithState to="/programmes/list">
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-dashed border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 transition-colors">
                    <div className="h-9 w-9 rounded-full bg-amber-500/10 flex items-center justify-center">
                        <Plus className="h-4.5 w-4.5 text-amber-600" />
                    </div>
                    <Typography
                        variant="body-xs"
                        className="text-center text-amber-700 dark:text-amber-400 font-medium"
                    >
                        {appContext.geti18n('home.programmes.active.new')}
                    </Typography>
                </div>
            </LinkWithState>
            <LinkWithState to="/communityProgrammes/list">
                <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-dashed border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors">
                    <div className="h-9 w-9 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <Search className="h-4.5 w-4.5 text-emerald-600" />
                    </div>
                    <Typography
                        variant="body-xs"
                        className="text-center text-emerald-700 dark:text-emerald-400 font-medium"
                    >
                        {appContext.geti18n('home.programmes.active.community')}
                    </Typography>
                </div>
            </LinkWithState>
        </div>
    )

    if (props.personalSchedule.length == 0) return emptySchedulePrompt

    const overflowLabels = {
        fallback: appContext.geti18n('programmes.listItem.side.text'),
        today: appContext.geti18n('programmes.listItem.side.today.text')
    }

    return (
        <div className="flex flex-col gap-2">
            {props.personalSchedule.map((e, idx) => {
                const programme = props.workoutList.find(
                    (s) => s.id == e.programId
                )

                if (!programme) return null

                const schedule = getActiveScheduleEntries(e, programme)
                let progressIdx = 0
                let nextSchedule = schedule.find((s, idx) => {
                    if (s.date >= today) {
                        progressIdx = idx
                        return true
                    }
                    return false
                })
                let lesson = programme.lessons.find(
                    (l) => l.id == nextSchedule?.lessonId
                )
                const historyDone =
                    e.history
                        ?.filter((h) => h.kind === 'exercise.finished')
                        .map((h) => h.referenceId) || []

                while (nextSchedule && lesson) {
                    const currentSchedule = nextSchedule
                    const countableExercises = lesson.items.filter(
                        (item) =>
                            isExerciseItem(item) && item.kind !== 'timeout'
                    )
                    const finishedLessons = countableExercises.filter((l) =>
                        historyDone.some((referenceId) => {
                            const resolved = resolveExerciseReference(
                                referenceId,
                                programme,
                                e.schedule
                            )
                            return (
                                resolved?.scheduleDayId ===
                                    currentSchedule.id &&
                                resolved.exerciseId === l.id
                            )
                        })
                    )
                    const finished =
                        countableExercises.length > 0 &&
                        finishedLessons.length === countableExercises.length
                    if (!finished) break
                    progressIdx += 1
                    nextSchedule = schedule[progressIdx]
                    lesson = programme.lessons.find(
                        (candidate) => candidate.id === nextSchedule?.lessonId
                    )
                }
                const programmeFinished = progressIdx >= schedule.length
                if (programmeFinished) return null

                let nextDate = 0
                if (nextSchedule?.date) nextDate = startOfDay(nextSchedule.date)

                const progressValue =
                    progressIdx && (progressIdx / schedule.length) * 100
                const isToday = nextDate === today

                return (
                    <LinkWithState
                        key={idx + '-personalSchedule'}
                        to={`/schedule/${e.programId}/details`}
                    >
                        {isToday ? (
                            <Card className="p-5 relative overflow-hidden rounded-xl">
                                <AnimatedGradientCover />
                                <CardContent className="relative z-10 p-0">
                                    <Typography
                                        variant="title-md"
                                        className="font-semibold leading-tight mb-1.5"
                                    >
                                        {programme?.name}
                                    </Typography>
                                    <div className="flex flex-col gap-0.5 mb-3">
                                        <span className="text-[10px] font-black uppercase text-white/60 tracking-wider">
                                            TODAY
                                        </span>
                                        <Typography
                                            variant="title-lg"
                                            className="font-bold leading-none"
                                        >
                                            {lesson && lesson.name}
                                        </Typography>
                                    </div>
                                </CardContent>
                                <CardContent className="relative z-10 p-0 space-y-0.5">
                                    {lesson &&
                                        lesson.items.map((e, idx) => {
                                            if (
                                                !isExerciseItem(e) ||
                                                e.kind === 'timeout'
                                            )
                                                return
                                            const reps = e.sets
                                                .map((ee) => ee.repetitions)
                                                .reduce(
                                                    (acc, curr) => acc! + curr!,
                                                    0
                                                )
                                            return (
                                                <Typography
                                                    key={
                                                        idx + '-lesson-content'
                                                    }
                                                    variant="body-sm"
                                                    className="font-medium flex items-baseline gap-2"
                                                >
                                                    <span className="font-bold tabular-nums text-xs opacity-70 min-w-[2rem]">
                                                        {e.sets.length}×
                                                        {reps ? reps : ''}
                                                    </span>
                                                    <span className="text-foreground">
                                                        {exerciseLabel(
                                                            e,
                                                            appContext.getExercise,
                                                            appContext.geti18n
                                                        )}
                                                    </span>
                                                </Typography>
                                            )
                                        })}
                                </CardContent>
                                <CardContent className="relative z-10 p-0 mt-3">
                                    {props.isActive && (
                                        <Progress
                                            value={progressValue}
                                            className="h-1 rounded-full"
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="flex flex-row border rounded-xl overflow-hidden hover:border-primary/20 transition-colors">
                                <CardContent className="flex-1 p-3.5">
                                    <Typography
                                        variant="title-sm"
                                        className="font-bold leading-tight"
                                    >
                                        {programme?.name}
                                    </Typography>
                                    <Typography
                                        variant="body-xs"
                                        className="mt-0.5"
                                    >
                                        {lesson && lesson.name}
                                    </Typography>
                                    {props.isActive && (
                                        <Progress
                                            value={progressValue}
                                            className="h-1 mt-2 rounded-full"
                                        />
                                    )}
                                </CardContent>
                                <ScheduleOverflow
                                    nextDate={nextDate}
                                    today={today}
                                    fallbackLabel={overflowLabels.fallback}
                                    todayLabel={overflowLabels.today}
                                />
                            </Card>
                        )}
                    </LinkWithState>
                )
            })}
        </div>
    )
}

export default ListItem
